import { useState } from "react";
import { Plus } from "lucide-react";
import { api, type BudgetItemDto, type IncomeRecordDto, type PortalPayload } from "../../lib/api";
import { formatINR } from "../../lib/format";
import {
  ActionMessage,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  MoneyInput,
  PanelSection,
  RowActions,
  Select,
  useAdminAction,
} from "./shared";

interface ItemForm {
  categoryId: number;
  description: string;
  quantity: number;
  unit: string;
  unitCostPaise: number | null;
  isPerAttendee: boolean;
  estimatedPaise: number | null;
  actualPaise: number | null;
  vendorRef: string;
  taxTreatment: string;
  gstRatePercent: string;
  paymentStatus: string;
  notes: string;
}

function itemToForm(it: BudgetItemDto): ItemForm {
  return {
    categoryId: it.categoryId,
    description: it.description,
    quantity: it.quantity,
    unit: it.unit,
    unitCostPaise: it.unitCostPaise,
    isPerAttendee: it.isPerAttendee,
    estimatedPaise: it.estimatedPaise,
    actualPaise: it.actualPaise,
    vendorRef: it.vendorRef ?? "",
    taxTreatment: it.taxTreatment,
    gstRatePercent: it.gstRateBps !== null ? String(it.gstRateBps / 100) : "",
    paymentStatus: it.paymentStatus,
    notes: it.notes,
  };
}

const emptyItemForm: ItemForm = {
  categoryId: 0,
  description: "",
  quantity: 1,
  unit: "unit",
  unitCostPaise: null,
  isPerAttendee: false,
  estimatedPaise: null,
  actualPaise: null,
  vendorRef: "",
  taxTreatment: "unknown",
  gstRatePercent: "",
  paymentStatus: "pending",
  notes: "",
};

export default function BudgetPanel({
  data,
  onSaved,
}: {
  data: PortalPayload;
  onSaved: () => void;
}) {
  const { message, busy, run } = useAdminAction();
  const [newCat, setNewCat] = useState("");
  const [itemTarget, setItemTarget] = useState<BudgetItemDto | "new" | null>(null);
  const [itemForm, setItemForm] = useState<ItemForm>(emptyItemForm);
  const [incomeTarget, setIncomeTarget] = useState<IncomeRecordDto | "new" | null>(null);
  const [incomeForm, setIncomeForm] = useState({
    source: "",
    description: "",
    amountPaise: 0,
    kind: "other",
    confirmed: false,
    receivedAt: "",
  });

  const s = data.budgetSummary;

  const addCategory = () =>
    run(async () => {
      await api.adminSend("/budget/categories", "POST", { name: newCat });
      setNewCat("");
      onSaved();
    }, "Budget category added.");

  const saveItem = () =>
    run(async () => {
      const payload = {
        categoryId: itemForm.categoryId,
        description: itemForm.description,
        quantity: Number(itemForm.quantity),
        unit: itemForm.unit,
        unitCostPaise: itemForm.unitCostPaise ?? 0,
        isPerAttendee: itemForm.isPerAttendee,
        estimatedPaise: itemForm.estimatedPaise,
        actualPaise: itemForm.actualPaise,
        vendorRef: itemForm.vendorRef || null,
        taxTreatment: itemForm.taxTreatment,
        gstRateBps: itemForm.gstRatePercent ? Math.round(Number(itemForm.gstRatePercent) * 100) : null,
        paymentStatus: itemForm.paymentStatus,
        notes: itemForm.notes,
      };
      if (itemTarget === "new") await api.adminSend("/budget/items", "POST", payload);
      else if (itemTarget) await api.adminSend(`/budget/items/${itemTarget.id}`, "PUT", payload);
      setItemTarget(null);
      onSaved();
    }, "Budget item saved.");

  const saveIncome = () =>
    run(async () => {
      const payload = {
        source: incomeForm.source,
        description: incomeForm.description,
        amountPaise: incomeForm.amountPaise ?? 0,
        kind: incomeForm.kind,
        confirmed: incomeForm.confirmed,
        receivedAt: incomeForm.receivedAt ? new Date(incomeForm.receivedAt).toISOString() : null,
      };
      if (incomeTarget === "new") await api.adminSend("/income", "POST", payload);
      else if (incomeTarget) await api.adminSend(`/income/${incomeTarget.id}`, "PUT", payload);
      setIncomeTarget(null);
      onSaved();
    }, "Income record saved.");

  return (
    <PanelSection
      title="Budget line items & actuals"
      description="Estimated amounts are planning figures; record actual amounts (from bills/receipts) as they happen — the surplus calculation uses actuals only."
      action={
        <Button
          onClick={() => {
            setItemForm({ ...emptyItemForm, categoryId: data.budgetCategories[0]?.id ?? 0 });
            setItemTarget("new");
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add budget item
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-wandor-muted">Total estimated</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatINR(s.totalEstimatedPaise)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-wandor-muted">Contingency allowance</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatINR(s.contingencyAllowancePaise)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-wandor-muted">Total actual</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatINR(s.totalActualPaise)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-wandor-muted">Recommended contribution</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatINR(s.recommendedContributionPaise)}</p>
        </Card>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="New budget category" hint="Food & beverages, Venue, Decorations, Contingency, …">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} maxLength={80} />
          </Field>
        </div>
        <Button onClick={addCategory} loading={busy} disabled={!newCat.trim()}>
          Add category
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {data.budgetCategories.map((c) => (
          <span key={c.id} className="rounded-full bg-white/[0.06] px-3 py-1 text-xs text-wandor-text">
            {c.name}
          </span>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-wandor-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Category</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Description</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Qty</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Unit cost</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Estimated</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Actual</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Tax</th>
              <th scope="col" className="px-4 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {data.budgetItems.map((it) => (
              <tr key={it.id} className="border-b border-white/5">
                <td className="px-4 py-2.5 text-wandor-muted">{it.categoryName}</td>
                <td className="px-4 py-2.5 text-wandor-text">{it.description}</td>
                <td className="px-4 py-2.5 tabular-nums text-wandor-muted">
                  {it.quantity} {it.unit}
                </td>
                <td className="px-4 py-2.5 tabular-nums text-wandor-muted">{formatINR(it.unitCostPaise)}</td>
                <td className="px-4 py-2.5 tabular-nums text-white">
                  {formatINR(it.estimatedPaise ?? it.quantity * it.unitCostPaise)}
                </td>
                <td className="px-4 py-2.5 tabular-nums text-wandor-text">{formatINR(it.actualPaise)}</td>
                <td className="px-4 py-2.5 text-xs text-wandor-muted">
                  {it.taxTreatment}
                  {it.gstRateBps ? ` ${it.gstRateBps / 100}%` : ""}
                </td>
                <td className="px-4 py-2.5">
                  <RowActions
                    onEdit={() => {
                      setItemForm(itemToForm(it));
                      setItemTarget(it);
                    }}
                    onDelete={() => {
                      if (window.confirm(`Delete budget item “${it.description}”?`)) {
                        void run(async () => {
                          await api.adminSend(`/budget/items/${it.id}`, "DELETE");
                          onSaved();
                        }, "Budget item deleted.");
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mt-8 text-lg font-semibold text-white">Income & funding records</h3>
      <p className="mt-1 text-sm text-wandor-muted">
        Confirmed = money actually received. Only confirmed income counts toward the final settlement.
      </p>
      <div className="mt-3">
        <Button
          variant="secondary"
          onClick={() => {
            setIncomeForm({ source: "", description: "", amountPaise: 0, kind: "other", confirmed: false, receivedAt: "" });
            setIncomeTarget("new");
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add income record
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {data.incomeRecords.map((r) => (
          <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <p className="font-medium text-white">
                {r.source} · {formatINR(r.amountPaise)}
              </p>
              <p className="text-sm text-wandor-muted">
                {r.kind} · {r.confirmed ? "confirmed" : "pledged (not received)"}
                {r.description ? ` · ${r.description}` : ""}
              </p>
            </div>
            <RowActions
              onEdit={() => {
                setIncomeForm({
                  source: r.source,
                  description: r.description,
                  amountPaise: r.amountPaise,
                  kind: r.kind,
                  confirmed: r.confirmed,
                  receivedAt: r.receivedAt ? r.receivedAt.slice(0, 16) : "",
                });
                setIncomeTarget(r);
              }}
              onDelete={() => {
                if (window.confirm(`Delete income record “${r.source}”?`)) {
                  void run(async () => {
                    await api.adminSend(`/income/${r.id}`, "DELETE");
                    onSaved();
                  }, "Income record deleted.");
                }
              }}
            />
          </Card>
        ))}
      </div>

      <ActionMessage message={message} />

      {itemTarget && (
        <Modal
          title={itemTarget === "new" ? "Add budget item" : `Edit “${itemTarget.description}”`}
          onClose={() => setItemTarget(null)}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Description" htmlFor="bi-desc">
                <Input
                  id="bi-desc"
                  value={itemForm.description}
                  onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={300}
                />
              </Field>
            </div>
            <Field label="Category" htmlFor="bi-cat">
              <Select
                id="bi-cat"
                value={itemForm.categoryId}
                onChange={(e) => setItemForm((f) => ({ ...f, categoryId: Number(e.target.value) }))}
              >
                {data.budgetCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Vendor / quote reference" htmlFor="bi-vendor">
              <Input id="bi-vendor" value={itemForm.vendorRef} onChange={(e) => setItemForm((f) => ({ ...f, vendorRef: e.target.value }))} maxLength={200} />
            </Field>
            <Field label="Quantity" htmlFor="bi-qty">
              <Input id="bi-qty" type="number" min={0} value={itemForm.quantity} onChange={(e) => setItemForm((f) => ({ ...f, quantity: Number(e.target.value) }))} />
            </Field>
            <Field label="Unit" htmlFor="bi-unit">
              <Input id="bi-unit" value={itemForm.unit} onChange={(e) => setItemForm((f) => ({ ...f, unit: e.target.value }))} maxLength={40} />
            </Field>
            <MoneyInput
              label="Unit cost (₹)"
              paise={itemForm.unitCostPaise}
              onChangePaise={(p) => setItemForm((f) => ({ ...f, unitCostPaise: p }))}
            />
            <MoneyInput
              label="Estimated amount (₹, optional override)"
              paise={itemForm.estimatedPaise}
              onChangePaise={(p) => setItemForm((f) => ({ ...f, estimatedPaise: p }))}
              hint="Leave empty to compute quantity × unit cost."
            />
            <MoneyInput
              label="Actual amount (₹)"
              paise={itemForm.actualPaise}
              onChangePaise={(p) => setItemForm((f) => ({ ...f, actualPaise: p }))}
              hint="Recorded bill/receipt amount."
            />
            <Field label="Tax treatment (as quoted)" htmlFor="bi-tax">
              <Select id="bi-tax" value={itemForm.taxTreatment} onChange={(e) => setItemForm((f) => ({ ...f, taxTreatment: e.target.value }))}>
                <option value="unknown">Unknown — verify on invoice</option>
                <option value="inclusive">Tax-inclusive (GST inside price)</option>
                <option value="exclusive">Tax-exclusive (GST added)</option>
                <option value="exempt">Exempt (as quoted)</option>
              </Select>
            </Field>
            <Field label="GST rate (%, as quoted)" htmlFor="bi-gst">
              <Input id="bi-gst" type="number" min={0} max={28} step="0.01" value={itemForm.gstRatePercent} onChange={(e) => setItemForm((f) => ({ ...f, gstRatePercent: e.target.value }))} />
            </Field>
            <Field label="Payment status" htmlFor="bi-pay">
              <Select id="bi-pay" value={itemForm.paymentStatus} onChange={(e) => setItemForm((f) => ({ ...f, paymentStatus: e.target.value }))}>
                <option value="pending">Pending</option>
                <option value="partial">Partially paid</option>
                <option value="paid">Paid</option>
                <option value="na">Not applicable</option>
              </Select>
            </Field>
            <Checkbox
              label="Per-attendee cost (variable)"
              checked={itemForm.isPerAttendee}
              onChange={(e) => setItemForm((f) => ({ ...f, isPerAttendee: e.target.checked }))}
            />
            <div className="sm:col-span-2">
              <Field label="Notes">
                <textarea
                  className="w-full min-h-[60px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={itemForm.notes}
                  onChange={(e) => setItemForm((f) => ({ ...f, notes: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setItemTarget(null)}>Cancel</Button>
            <Button onClick={saveItem} loading={busy}>Save item</Button>
          </div>
        </Modal>
      )}

      {incomeTarget && (
        <Modal
          title={incomeTarget === "new" ? "Add income record" : `Edit “${incomeTarget.source}”`}
          onClose={() => setIncomeTarget(null)}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Source" htmlFor="in-src">
              <Input id="in-src" value={incomeForm.source} onChange={(e) => setIncomeForm((f) => ({ ...f, source: e.target.value }))} maxLength={160} />
            </Field>
            <MoneyInput
              label="Amount (₹)"
              paise={incomeForm.amountPaise}
              onChangePaise={(p) => setIncomeForm((f) => ({ ...f, amountPaise: p ?? 0 }))}
            />
            <Field label="Kind" htmlFor="in-kind">
              <Select id="in-kind" value={incomeForm.kind} onChange={(e) => setIncomeForm((f) => ({ ...f, kind: e.target.value }))}>
                <option value="contribution">Student contribution</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <Field label="Received at" htmlFor="in-when">
              <Input id="in-when" type="datetime-local" value={incomeForm.receivedAt} onChange={(e) => setIncomeForm((f) => ({ ...f, receivedAt: e.target.value }))} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea
                  className="w-full min-h-[60px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
            <Checkbox
              label="Confirmed (money actually received)"
              checked={incomeForm.confirmed}
              onChange={(e) => setIncomeForm((f) => ({ ...f, confirmed: e.target.checked }))}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIncomeTarget(null)}>Cancel</Button>
            <Button onClick={saveIncome} loading={busy}>Save record</Button>
          </div>
        </Modal>
      )}
    </PanelSection>
  );
}
